/**
 * Profile Modal
 * Overlay for editing profile: avatar, name, password, LinkedIn URL
 */

import React, { useState, useEffect } from 'react';
import { X, User, Lock, Linkedin, Camera } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { getProfile, updateProfile } from '../../utils/api';
import { supabase } from '../../lib/supabase';
import './ProfileModal.css';

const ProfileModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [form, setForm] = useState({
    full_name: '',
    avatar_url: '',
    linkedin_url: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    if (!isOpen || !user) return;
    let cancelled = false;
    setLoading(true);
    setMessage({ type: '', text: '' });
    getProfile()
      .then((profile) => {
        if (!cancelled) {
          setForm((f) => ({
            ...f,
            full_name: profile.full_name ?? user.user_metadata?.full_name ?? '',
            avatar_url: profile.avatar_url ?? '',
            linkedin_url: profile.linkedin_url ?? '',
            current_password: '',
            new_password: '',
            confirm_password: '',
          }));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setForm((f) => ({
            ...f,
            full_name: user.user_metadata?.full_name ?? '',
            avatar_url: '',
            linkedin_url: '',
          }));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [isOpen, user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setMessage({ type: '', text: '' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });
    setSaving(true);
    try {
      if (form.new_password) {
        if (form.new_password !== form.confirm_password) {
          setMessage({ type: 'error', text: 'New password and confirmation do not match.' });
          setSaving(false);
          return;
        }
        if (!supabase) {
          setMessage({ type: 'error', text: 'Cannot update password: auth not configured.' });
          setSaving(false);
          return;
        }
        const { error } = await supabase.auth.updateUser({ password: form.new_password });
        if (error) {
          setMessage({ type: 'error', text: error.message || 'Failed to update password.' });
          setSaving(false);
          return;
        }
      }
      await updateProfile({
        full_name: form.full_name || undefined,
        avatar_url: form.avatar_url || undefined,
        linkedin_url: form.linkedin_url || undefined,
      });
      setMessage({ type: 'success', text: 'Profile saved.' });
      setForm((f) => ({ ...f, current_password: '', new_password: '', confirm_password: '' }));
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || err.message || 'Failed to save profile.' });
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="profile-modal-overlay" onClick={onClose}>
      <div className="profile-modal" onClick={(e) => e.stopPropagation()}>
        <div className="profile-modal-header">
          <h2>Profile</h2>
          <button type="button" className="profile-modal-close" onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </div>
        {loading ? (
          <div className="profile-modal-loading">Loading...</div>
        ) : (
          <form className="profile-modal-form" onSubmit={handleSubmit}>
            <div className="profile-modal-section">
              <label className="profile-modal-label">
                <Camera size={18} />
                Profile picture
              </label>
              <div className="profile-modal-avatar-row">
                <div className="profile-modal-avatar">
                  {form.avatar_url ? (
                    <img src={form.avatar_url} alt="Avatar" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <User size={40} />
                  )}
                </div>
                <input
                  type="url"
                  name="avatar_url"
                  value={form.avatar_url}
                  onChange={handleChange}
                  placeholder="Image URL"
                  className="profile-modal-input"
                />
              </div>
            </div>
            <div className="profile-modal-section">
              <label className="profile-modal-label">Email</label>
              <div className="profile-modal-email">{user?.email}</div>
            </div>
            <div className="profile-modal-section">
              <label className="profile-modal-label" htmlFor="profile-full_name">
                <User size={18} />
                Name
              </label>
              <input
                id="profile-full_name"
                type="text"
                name="full_name"
                value={form.full_name}
                onChange={handleChange}
                placeholder="Your name"
                className="profile-modal-input"
              />
            </div>
            <div className="profile-modal-section">
              <label className="profile-modal-label" htmlFor="profile-linkedin">
                <Linkedin size={18} />
                LinkedIn profile URL
              </label>
              <input
                id="profile-linkedin"
                type="url"
                name="linkedin_url"
                value={form.linkedin_url}
                onChange={handleChange}
                placeholder="https://linkedin.com/in/yourprofile"
                className="profile-modal-input"
              />
            </div>
            <div className="profile-modal-section">
              <label className="profile-modal-label">
                <Lock size={18} />
                Change password
              </label>
              <input
                type="password"
                name="current_password"
                value={form.current_password}
                onChange={handleChange}
                placeholder="Current password (optional)"
                className="profile-modal-input"
                autoComplete="current-password"
              />
              <input
                type="password"
                name="new_password"
                value={form.new_password}
                onChange={handleChange}
                placeholder="New password"
                className="profile-modal-input"
                autoComplete="new-password"
              />
              <input
                type="password"
                name="confirm_password"
                value={form.confirm_password}
                onChange={handleChange}
                placeholder="Confirm new password"
                className="profile-modal-input"
                autoComplete="new-password"
              />
            </div>
            {message.text && (
              <div className={`profile-modal-message profile-modal-message-${message.type}`}>
                {message.text}
              </div>
            )}
            <div className="profile-modal-actions">
              <button type="button" className="profile-modal-btn profile-modal-btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="profile-modal-btn profile-modal-btn-primary" disabled={saving}>
                {saving ? 'Saving...' : 'Save changes'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default ProfileModal;
